// Pure logic for the searchable customer picker (CustomerSearchSelect). Kept
// React-free so the matching/labeling logic is unit-testable in node vitest
// (same convention as sku-search.ts / format.ts).

import type { Customer } from '@gutterinv/shared';

/** Closed-state / option label. Single source of truth for BOTH pickers
 *  (PricingCalculator + CreateJobDialog) — keep it consistent. */
export function formatCustomerLabel(c: Pick<Customer, 'name' | 'type'>): string {
  return `${c.name} (${c.type})`;
}

/**
 * Filter + rank customers for a free-text query.
 * - empty / whitespace query → the full list UNCHANGED (preserves caller order).
 * - matches case-insensitively against name, companyName, contactName, email.
 * - ranks: name-prefix (0) < name-substring (1) < other-field substring (2);
 *   stable within a tier (preserves input order).
 */
export function filterCustomers(customers: Customer[], query: string): Customer[] {
  const q = query.trim().toLowerCase();
  if (!q) return customers;
  const ranked: Array<{ c: Customer; rank: number; i: number }> = [];
  customers.forEach((c, i) => {
    const name = c.name.toLowerCase();
    const company = c.companyName?.toLowerCase() ?? '';
    const contact = c.contactName?.toLowerCase() ?? '';
    const email = c.email?.toLowerCase() ?? '';
    let rank = -1;
    if (name.startsWith(q)) rank = 0;
    else if (name.includes(q)) rank = 1;
    else if (company.includes(q) || contact.includes(q) || email.includes(q)) rank = 2;
    if (rank >= 0) ranked.push({ c, rank, i });
  });
  ranked.sort((a, b) => a.rank - b.rank || a.i - b.i);
  return ranked.map((r) => r.c);
}

/**
 * Label for the closed trigger.
 * - no value → '' (placeholder shows).
 * - value found → its formatted label.
 * - value present but NOT in the list → fallback string; NEVER auto-clear an
 *   unrenderable selection (a seeded/pre-set customer must persist).
 */
export function resolveSelectedCustomerLabel(
  customers: Customer[],
  value: string,
  unavailableLabel = 'Unknown customer',
): string {
  if (!value) return '';
  const found = customers.find((c) => c.id === value);
  return found ? formatCustomerLabel(found) : unavailableLabel;
}
